import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import supabase from '@/lib/supabase';

interface AntiSpamConfig {
  jitterMs: number;
  dailyLimit: number;
  warmupCount: number;
  warmupDelayMs: number;
}

const defaults: AntiSpamConfig = {
  jitterMs: 5000,
  dailyLimit: 200,
  warmupCount: 50,
  warmupDelayMs: 12000,
};

export default function SettingsAntiSpamPage() {
  const { profile } = useAuth();

  const [config, setConfig] = useState<AntiSpamConfig>(defaults);
  const [originalConfig, setOriginalConfig] = useState<AntiSpamConfig>(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!profile?.company_id) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('whatsapp_configs')
      .select('anti_spam_jitter_ms, anti_spam_daily_limit, anti_spam_warmup_count, anti_spam_warmup_delay_ms')
      .eq('company_id', profile.company_id)
      .maybeSingle();

    if (!error && data) {
      const loaded = {
        jitterMs: data.anti_spam_jitter_ms ?? defaults.jitterMs,
        dailyLimit: data.anti_spam_daily_limit ?? defaults.dailyLimit,
        warmupCount: data.anti_spam_warmup_count ?? defaults.warmupCount,
        warmupDelayMs: data.anti_spam_warmup_delay_ms ?? defaults.warmupDelayMs,
      };
      setConfig(loaded);
      setOriginalConfig(loaded);
    }

    setLoading(false);
  }, [profile?.company_id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const hasChanges =
    config.jitterMs !== originalConfig.jitterMs ||
    config.dailyLimit !== originalConfig.dailyLimit ||
    config.warmupCount !== originalConfig.warmupCount ||
    config.warmupDelayMs !== originalConfig.warmupDelayMs;

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setSaved(false);

    const payload = {
      company_id: profile.company_id,
      anti_spam_jitter_ms: config.jitterMs,
      anti_spam_daily_limit: config.dailyLimit,
      anti_spam_warmup_count: config.warmupCount,
      anti_spam_warmup_delay_ms: config.warmupDelayMs,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('whatsapp_configs')
      .upsert(payload, { onConflict: 'company_id' });

    setSaving(false);

    if (error) {
      return;
    }

    setOriginalConfig({ ...config });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setConfig(defaults);
    setOriginalConfig(defaults);
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <div className="animate-pulse space-y-4">
          <div className="h-7 w-48 bg-background-200 rounded" />
          <div className="h-4 w-72 bg-background-200 rounded" />
          <div className="h-48 bg-background-200 rounded-xl" />
          <div className="h-48 bg-background-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Anti-Spam &amp; Envio Seguro</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Ajuste os delays, limites e warm-up para evitar bloqueios do WhatsApp
          </p>
        </div>
        <button
          onClick={handleReset}
          className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-foreground-700 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
        >
          <i className="ri-restart-line" />
          Restaurar padrões
        </button>
      </div>

      {/* Status banner */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border text-sm bg-accent-50 border-accent-200 text-accent-800">
        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-shield-check-line text-accent-600 text-lg" />
        </div>
        <div>
          <p className="font-semibold">5 camadas de proteção ativas</p>
          <p className="text-xs text-accent-600 mt-0.5">
            Jitter &middot; Spintax &middot; Warm-up &middot; Limite diário &middot; Variantes
          </p>
        </div>
      </div>

      {/* ─── JITTER ─── */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-shuffle-line text-accent-600 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold text-foreground-950">Delay Aleatório (Jitter)</h3>
            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
              Adiciona um atraso extra aleatório entre cada mensagem, imitando digitação humana.
              O WhatsApp detecta envios com intervalos fixos como comportamento de bot.
            </p>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground-700">
              Delay extra máximo
            </label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={15000}
                step={500}
                value={config.jitterMs}
                onChange={(e) => setConfig({ ...config, jitterMs: Number(e.target.value) })}
                className="w-36 h-2 rounded-full bg-background-200 appearance-none cursor-pointer accent-accent-500"
              />
              <input
                type="number"
                min={0}
                max={15000}
                step={100}
                value={config.jitterMs}
                onChange={(e) => setConfig({ ...config, jitterMs: Number(e.target.value) })}
                className="w-[90px] px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 text-center font-mono outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
              />
              <span className="text-xs text-foreground-500">ms</span>
            </div>
          </div>

          <div className="flex justify-between text-[10px] text-foreground-400">
            <span>0ms (desligado)</span>
            <span className="font-medium text-foreground-600">{config.jitterMs}ms → {(config.jitterMs / 1000).toFixed(1)}s extra</span>
            <span>15.000ms (máximo)</span>
          </div>

          <div className="bg-accent-50 border border-accent-100 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-accent-500 text-sm flex-shrink-0 mt-px" />
              <div className="text-xs text-accent-700">
                <p className="font-medium mb-0.5">Como funciona na prática</p>
                <p>
                  Delay base (~3-7s) + Jitter (0-{config.jitterMs}ms) = cada mensagem leva entre{' '}
                  <strong>3s e {(3 + config.jitterMs / 1000).toFixed(1)}s</strong> para ser enviada.
                  {config.jitterMs === 0 && ' Sem jitter, o intervalo fica fixo e previsível — evite isso.'}
                  {config.jitterMs < 2000 && config.jitterMs > 0 && ' Um jitter baixo ainda pode parecer robótico. Recomendado: 3000-8000ms.'}
                  {config.jitterMs >= 8000 && ' Jitter alto — mais seguro, mas o envio fica bem mais lento.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── WARM-UP ─── */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-temp-hot-line text-accent-600 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold text-foreground-950">Aquecimento (Warm-up)</h3>
            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
              As primeiras mensagens de cada lote usam delays maiores. Isso simula uma conta nova
              &quot;esquentando&quot; aos poucos — comportamento esperado pela Meta.
            </p>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground-700">
                Quantas mensagens no warm-up
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={500}
                  step={10}
                  value={config.warmupCount}
                  onChange={(e) => setConfig({ ...config, warmupCount: Number(e.target.value) })}
                  className="w-[90px] px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 text-center font-mono outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
                <span className="text-xs text-foreground-500">mensagens</span>
              </div>
              <p className="text-[10px] text-foreground-400">
                {config.warmupCount === 0
                  ? '0 = sem warm-up (não recomendado)'
                  : `As primeiras ${config.warmupCount} mensagens terão delay estendido`}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground-700">
                Delay durante warm-up
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={2000}
                  max={60000}
                  step={500}
                  value={config.warmupDelayMs}
                  onChange={(e) => setConfig({ ...config, warmupDelayMs: Number(e.target.value) })}
                  className="w-[90px] px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 text-center font-mono outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
                <span className="text-xs text-foreground-500">ms</span>
              </div>
              <p className="text-[10px] text-foreground-400">
                {(config.warmupDelayMs / 1000).toFixed(1)}s entre cada mensagem no warm-up
              </p>
            </div>
          </div>

          {config.warmupCount > 0 && (
            <div className="bg-accent-50 border border-accent-100 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-accent-500 text-sm flex-shrink-0 mt-px" />
                <div className="text-xs text-accent-700">
                  <p className="font-medium mb-0.5">Tempo total estimado do warm-up</p>
                  <p>
                    {config.warmupCount} mensagens &times; {config.warmupDelayMs}ms ={' '}
                    <strong>
                      {Math.round((config.warmupCount * config.warmupDelayMs) / 1000 / 60)} minutos{' '}
                    </strong>
                    para aquecer. Depois disso, o delay cai para o ritmo normal (com jitter).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── DAILY LIMIT ─── */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-speed-up-line text-accent-600 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold text-foreground-950">Limite Diário de Envios</h3>
            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
              Número máximo de mensagens que o sistema envia por dia. Ao atingir o limite,
              a campanha pausa automaticamente e retoma no dia seguinte.
            </p>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground-700">
              Máximo de mensagens por dia
            </label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={1000}
                step={10}
                value={config.dailyLimit}
                onChange={(e) => setConfig({ ...config, dailyLimit: Number(e.target.value) })}
                className="w-36 h-2 rounded-full bg-background-200 appearance-none cursor-pointer accent-accent-500"
              />
              <input
                type="number"
                min={0}
                max={9999}
                step={10}
                value={config.dailyLimit}
                onChange={(e) => setConfig({ ...config, dailyLimit: Number(e.target.value) })}
                className="w-[90px] px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 text-center font-mono outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
              />
              <span className="text-xs text-foreground-500">/dia</span>
            </div>
          </div>

          <div className="flex justify-between text-[10px] text-foreground-400">
            <span>0 (sem limite)</span>
            <span className="font-medium text-foreground-600">
              {config.dailyLimit === 0 ? 'Ilimitado — cuidado!' : `${config.dailyLimit} mensagens/dia`}
            </span>
            <span>1.000 (máximo seguro)</span>
          </div>

          <div className={`rounded-lg p-3 border ${
            config.dailyLimit === 0
              ? 'bg-red-50 border-red-200'
              : config.dailyLimit > 300
                ? 'bg-amber-50 border-amber-200'
                : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-start gap-2">
              <i className={`text-sm flex-shrink-0 mt-px ${
                config.dailyLimit === 0
                  ? 'ri-error-warning-fill text-red-500'
                  : config.dailyLimit > 300
                    ? 'ri-alert-line text-amber-500'
                    : 'ri-checkbox-circle-fill text-green-500'
              }`} />
              <div className={`text-xs ${
                config.dailyLimit === 0
                  ? 'text-red-700'
                  : config.dailyLimit > 300
                    ? 'text-amber-700'
                    : 'text-green-700'
              }`}>
                {config.dailyLimit === 0 && (
                  <>
                    <p className="font-medium mb-0.5">Sem limite diário — risco alto de bloqueio!</p>
                    <p>Recomendamos definir um limite entre 100-300 mensagens/dia para evitar que o WhatsApp detecte envio em massa.</p>
                  </>
                )}
                {config.dailyLimit > 0 && config.dailyLimit <= 150 && (
                  <>
                    <p className="font-medium mb-0.5">Limite conservador — muito seguro</p>
                    <p>Ideal para números novos ou quando você quer máxima proteção. O envio será mais lento mas muito mais seguro.</p>
                  </>
                )}
                {config.dailyLimit > 150 && config.dailyLimit <= 300 && (
                  <>
                    <p className="font-medium mb-0.5">Limite equilibrado — recomendado</p>
                    <p>Bom equilíbrio entre velocidade e segurança. Funciona bem para a maioria das campanhas.</p>
                  </>
                )}
                {config.dailyLimit > 300 && (
                  <>
                    <p className="font-medium mb-0.5">Limite alto — use com moderação</p>
                    <p>Quanto mais mensagens por dia, maior a chance do WhatsApp notar. Só use esse volume se sua conta já estiver bem aquecida.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SIMULADOR ─── */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-timer-line text-accent-600 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold text-foreground-950">Simulador de Tempo</h3>
            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
              Estimativa de quanto tempo levaria para enviar 100 mensagens com as configurações atuais
            </p>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg p-4">
          {(() => {
            const baseDelayMin = 3;
            const baseDelayMax = 7;
            const avgBaseDelay = (baseDelayMin + baseDelayMax) / 2;
            const avgJitter = config.jitterMs / 1000 / 2;
            const warmupExtra = config.warmupDelayMs / 1000 - avgBaseDelay;

            let totalSeconds: number;
            if (config.warmupCount >= 100) {
              totalSeconds = 100 * (config.warmupDelayMs / 1000);
            } else {
              const warmupSecs = config.warmupCount * (config.warmupDelayMs / 1000);
              const normalCount = 100 - config.warmupCount;
              const normalSecs = normalCount * (avgBaseDelay + avgJitter);
              totalSeconds = warmupSecs + normalSecs;
            }

            const minutes = Math.floor(totalSeconds / 60);
            const seconds = Math.round(totalSeconds % 60);

            const effectiveDelayWarmup = (config.warmupDelayMs / 1000).toFixed(1);
            const effectiveDelayNormal = (avgBaseDelay + avgJitter).toFixed(1);

            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Tempo estimado</p>
                    <p className="text-2xl font-bold text-foreground-950 font-mono">
                      {minutes > 0 ? `${minutes}min ` : ''}{seconds}s
                    </p>
                    <p className="text-[10px] text-foreground-400 mt-0.5">para 100 mensagens</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Ritmo médio</p>
                    <p className="text-2xl font-bold text-foreground-950 font-mono">
                      {effectiveDelayNormal}s
                    </p>
                    <p className="text-[10px] text-foreground-400 mt-0.5">por mensagem (após warm-up)</p>
                  </div>
                </div>

                <div className="text-xs text-foreground-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Delay base</span>
                    <span className="font-mono text-foreground-700">{baseDelayMin}s–{baseDelayMax}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Jitter médio extra</span>
                    <span className="font-mono text-foreground-700">+{avgJitter.toFixed(1)}s</span>
                  </div>
                  {config.warmupCount > 0 && (
                    <div className="flex justify-between text-accent-700">
                      <span>Warm-up ({config.warmupCount} msg)</span>
                      <span className="font-mono">{effectiveDelayWarmup}s cada</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ─── SAVE BAR ─── */}
      <div className="sticky bottom-0 z-10 bg-background-50 border-t border-background-200 -mx-6 px-6 py-4 flex items-center justify-between rounded-b-xl mt-6">
        <div>
          {saved && (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1 animate-pulse">
              <i className="ri-check-line" />
              Configurações salvas!
            </span>
          )}
          {!hasChanges && !saved && (
            <span className="text-xs text-foreground-400">Nenhuma alteração pendente</span>
          )}
          {hasChanges && (
            <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
              <i className="ri-edit-line" />
              Alterações não salvas
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setConfig(originalConfig);
            }}
            disabled={!hasChanges}
            className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-5 py-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin text-lg" /> Salvando...</>
            ) : (
              <><i className="ri-save-line text-lg" /> Salvar configurações</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}